import React, { useState } from 'react';
import { useMutationState } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import type { PostSignupRequest } from '@blockworks/platform/api/research/auth';
import { useTrack } from '@blockworks/platform/services/analytics';
import { openNewTab } from '@blockworks/platform/utils';
import { authMutations } from '@blockworks/session/mutations';
import { Button, Divider, FlexBox, Grid, Text } from '@blockworks/ui/components';
import { Form } from '@blockworks/ui/forms/form';
import { ArrowLeftIcon } from '@blockworks/ui/icon';

import { signupSchema } from '@/components/auth/forms/signup.schema';
import { OAuthButton } from '@/components/auth/oauth-buttons';
import { PasswordRequirements } from '@/components/auth/password-requirements';
import { SignupImageSlider } from '@/components/auth/signup-image-slider/signup-image-slider';
import AuthLayout from '@/layout/auth/auth-layout';
import { IMetaProps } from '@/layout/default-meta';
import { AnalyticsEvent } from '@/modules/utils/enums/events-tracking';
import ResearchConfig from '@/research-config';
import countryCodes from '@/utils/country-codes';
import { bwrToast } from '@/utils/toast-manager';

export const getServerSideProps = async () => {
    return {
        props: {
            meta: {
                title: 'Sign Up | Blockworks Research',
            },
        },
    };
};

const SignUpPage = () => {
    const router = useRouter();
    const track = useTrack();
    const [step, setStep] = useState<string>('account');

    const signUpForm = Form.use({
        values: {
            email: '',
            password: '',
            firstname: '',
            lastname: '',
            countryCode: '+1',
            phoneNumber: '',
            company: '',
        },
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: Form.resolvers.yup(signupSchema),
    });

    const handleSetStep = (nextStep: string, validateFields?: (keyof PostSignupRequest)[]) => async () => {
        if (!validateFields) return setStep(nextStep);

        const isValid = await signUpForm.trigger(validateFields);
        if (isValid) setStep(nextStep);
    };

    const { errors } = signUpForm.formState;
    const isAccountInvalid = errors.email || errors.password;

    const signin = authMutations.signin.use();
    const anySignIn = useMutationState({ filters: { mutationKey: authMutations.signin.use.getKey() } });

    const signup = authMutations.signup.use({
        onSuccess: async ({ data }, variables) => {
            if (data.status === 0) {
                bwrToast.danger(data.message);
                handleSetStep('account')();
                return;
            }
            if (variables) {
                await signin.mutate({
                    provider: 'credentials',
                    options: {
                        email: variables.email,
                        password: variables.password,
                        redirect: true,
                        callbackUrl: '/sign-up/select-plan',
                    },
                });

                track(AnalyticsEvent.successfulSignup, {
                    source: router.asPath,
                    email: variables.email,
                });
            }
        },
    });

    const onSubmit = signUpForm.handleSubmit(values => {
        signup.mutate(values);
    });

    return (
        <FlexBox col w="full">
            {step === 'account' && (
                <Grid gap={2}>
                    <Grid.Col colSpan={12}>
                        <OAuthButton provider="google">Sign Up With Google</OAuthButton>
                    </Grid.Col>
                    <Grid.Col colSpan={12}>
                        <OAuthButton provider="microsoft-entra-id">Sign Up With Outlook</OAuthButton>
                    </Grid.Col>
                    <Grid.Col colSpan={12}>
                        <Divider betweenText="or" />
                    </Grid.Col>
                </Grid>
            )}
            <Form className="w-full" onSubmit={onSubmit}>
                {step === 'account' && (
                    <Grid gap={2}>
                        <Grid.Col colSpan={12}>
                            <Form.Input
                                name="email"
                                control={signUpForm.control}
                                placeholder="Enter your email address"
                                type="email"
                            />
                        </Grid.Col>
                        <Grid.Col colSpan={12}>
                            <Form.Input
                                name="password"
                                control={signUpForm.control}
                                placeholder="Enter your password"
                                type="password"
                                hideErrorMessage
                            />
                        </Grid.Col>
                        <Grid.Col colSpan={12}>
                            <PasswordRequirements password={signUpForm.watch('password')!} />
                        </Grid.Col>
                        <Grid.Col colSpan={12} pt={3}>
                            <Button
                                intent="primary"
                                w="full"
                                center
                                type="button"
                                disabled={
                                    !!isAccountInvalid ||
                                    anySignIn.some(({ status }) => ['pending', 'success'].includes(status))
                                }
                                onClick={handleSetStep('profile', ['email', 'password'])}
                            >
                                Continue
                            </Button>
                            <Text size="xxs" mt={3}>
                                By creating an account, you agree to our Privacy Policy and Terms of Service.
                            </Text>
                        </Grid.Col>
                    </Grid>
                )}
                {step === 'profile' && (
                    <Grid>
                        <Grid.Col colSpan={12}>
                            <Button
                                type="button"
                                variant="link"
                                intent="primary"
                                size="flush"
                                leadingIcon={ArrowLeftIcon}
                                onClick={handleSetStep('account')}
                            >
                                Back
                            </Button>
                        </Grid.Col>
                        <Grid.Col colSpan={6}>
                            <Form.Input name="firstname" control={signUpForm.control} placeholder="First name" />
                        </Grid.Col>
                        <Grid.Col colSpan={6}>
                            <Form.Input name="lastname" control={signUpForm.control} placeholder="Last name" />
                        </Grid.Col>
                        <Grid.Col colSpan={12}>
                            <Form.Input name="company" control={signUpForm.control} placeholder="Company" />
                        </Grid.Col>
                        <Grid.Col colSpan={12} gap={2} display="flex">
                            <div className="w-[80px]">
                                <Form.Select
                                    name="countryCode"
                                    control={signUpForm.control}
                                    options={countryCodes}
                                    placeholder="Company"
                                    hideIndicator
                                    fullWidth
                                />
                            </div>
                            <Form.Input
                                name="phoneNumber"
                                control={signUpForm.control}
                                placeholder="Enter phone number"
                                type="tel"
                            />
                        </Grid.Col>
                        <Grid.Col colSpan={12}>
                            <Form.Submit
                                w="full"
                                center
                                loading={
                                    signup.isPending ||
                                    anySignIn.some(({ status }) => ['pending', 'success'].includes(status))
                                }
                            >
                                Create Account
                            </Form.Submit>
                        </Grid.Col>
                    </Grid>
                )}
            </Form>
            <FlexBox gap="xs" center w="full" mt={5}>
                <Text size="sm" color="muted">
                    Already have an account?
                </Text>
                <Button variant="link" intent="primary" size="flush" onClick={() => router.push('/sign-in')}>
                    Sign in
                </Button>
            </FlexBox>
        </FlexBox>
    );
};

SignUpPage.getLayout = (page: React.ReactElement, metaProps: IMetaProps) => {
    return (
        <AuthLayout
            metaProps={metaProps}
            headingText="Create an account"
            subheadingText="Sign up for Blockworks Research to access insights you can’t invest without."
            col
            yAlignCenter
            sidebarContent={<SignupImageSlider />}
            sidebarContentPosition="left"
            sidebarFooterContent={
                <Button
                    variant="outline"
                    borderRadius="md"
                    size="sm"
                    w="max"
                    onClick={openNewTab.with(`https://${ResearchConfig.prodBasename}/#faqs`)}
                    mx="auto"
                >
                    View FAQs
                </Button>
            }
        >
            {page}
        </AuthLayout>
    );
};

export default SignUpPage;
